(function () {
  'use strict';

  const SELECTOR_CONTAINER = '[data-infinite-container]';
  const SELECTOR_GRID = '#product-grid';
  const SELECTOR_SENTINEL = '#infinite-scroll-sentinel';
  const SELECTOR_NEXT = 'link[rel="next"]';

  /*
   * =========================================================
   * MUSIKMACHINE - COLLECTION INFINITE SCROLL
   *
   * - 42 produits initiaux
   * - préchargement silencieux du lot suivant
   * - affichage anticipé
   * - indicateur de chargement clair
   * - indication de fin de collection
   * - restauration de position après retour navigateur
   * - compatible tri / filtres / re-render Shopify
   * =========================================================
   */

  const RESTORE_PREFIX = 'mm_collection_restore:';
  const RESTORE_MAX_AGE = 2 * 60 * 60 * 1000;

  // On commence à afficher le lot suivant avant que le client
  // atteigne réellement le bas.
  const LOAD_AHEAD_PX = 1800;

  // Petit délai avant préchargement pour laisser la page initiale
  // respirer (LCP / images / JS du thème).
  const PREFETCH_DELAY = 700;

  let loading = false;
  let reachedEnd = false;
  let observer = null;
  let totalProducts = 0;
  let isRestoring = false;

  const loadedURLs = new Set();

  /*
   * Cache mémoire du prochain lot :
   *
   * {
   *   url: "...?page=2",
   *   items: [li, li, ...],
   *   nextURL: "...?page=3"
   * }
   */
  let prefetchedPage = null;
  let prefetchPromise = null;


  /* =========================================================
     OUTILS GÉNÉRAUX
     ========================================================= */

  function parseIntSafe(v) {
    const n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }


  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  function nextFrame() {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }


  function normalizeURL(url) {
    if (!url) return null;

    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return url;
    }
  }


  /* =========================================================
     CLÉ DE COLLECTION / RESTAURATION
     ========================================================= */

  function getCollectionKey() {
    const url = new URL(window.location.href);

    // ?page= ne doit pas créer une collection différente.
    url.searchParams.delete('page');

    return RESTORE_PREFIX + url.pathname + url.search;
  }


  function normalizeProductPath(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch (e) {
      return url || '';
    }
  }


  function cameFromHistoryNavigation() {
    const nav = performance.getEntriesByType('navigation');

    if (nav && nav.length) {
      return nav[0].type === 'back_forward';
    }

    // Fallback anciens navigateurs
    if (performance.navigation) {
      return performance.navigation.type === 2;
    }

    return false;
  }


  /* =========================================================
     MÉMORISATION DE LA POSITION
     ========================================================= */

  function saveCollectionState(productLink) {
    const grid = document.querySelector(SELECTOR_GRID);

    if (!grid) return;

    const items = grid.querySelectorAll('li.grid__item');

    const state = {
      loadedCount: items.length,
      scrollY: window.scrollY,
      productUrl: productLink || '',
      timestamp: Date.now()
    };

    try {
      sessionStorage.setItem(
        getCollectionKey(),
        JSON.stringify(state)
      );
    } catch (e) {
      console.warn(
        '[infinite-collection] impossible de mémoriser la position',
        e
      );
    }
  }


  function getSavedCollectionState() {
    try {
      const raw = sessionStorage.getItem(getCollectionKey());

      if (!raw) return null;

      const state = JSON.parse(raw);

      if (
        !state ||
        !state.timestamp ||
        Date.now() - state.timestamp > RESTORE_MAX_AGE
      ) {
        sessionStorage.removeItem(getCollectionKey());
        return null;
      }

      return state;

    } catch (e) {
      return null;
    }
  }


  function findSavedProduct(grid, productUrl) {
    if (!productUrl) return null;

    const wantedPath = normalizeProductPath(productUrl);

    const links = grid.querySelectorAll(
      'a[href*="/products/"]'
    );

    for (const link of links) {
      if (normalizeProductPath(link.href) === wantedPath) {
        return link.closest('li.grid__item');
      }
    }

    return null;
  }


  /* =========================================================
     COMPTEURS
     ========================================================= */

  function extractTotalFromExistingText() {
    const el = document.querySelector(
      '#ProductCountDesktop, #ProductCount, #ProductCountTop, #ProductCountMobile'
    );

    if (!el) return 0;

    const nums = (el.textContent || '').match(/\d+/g);

    if (!nums || !nums.length) return 0;

    return Math.max(...nums.map(parseIntSafe));
  }


  function updateCount(grid) {
    if (!grid) return;

    const loaded =
      grid.querySelectorAll('li.grid__item').length;

    const container =
      document.querySelector(SELECTOR_CONTAINER);

    const total = parseInt(
      container?.dataset?.totalProducts || '0',
      10
    );

    const labels = document.querySelectorAll(
      '#ProductCountDesktop, #ProductCount, #ProductCountTop, #ProductCountMobile'
    );

    labels.forEach(el => {
      if (!el) return;

      el.textContent =
        total > 0
          ? `${loaded} sur ${total}`
          : `${loaded}`;
    });

    if (window.__updateTopCount) {
      window.__updateTopCount();
    }
  }


  /* =========================================================
     URL PAGE SUIVANTE
     ========================================================= */

  function getNextURL(container) {
    return container?.dataset?.nextUrl || null;
  }


  function setNextURL(container, url) {
    if (!container) return;

    if (url) {
      container.dataset.nextUrl = url;

      let el =
        container.querySelector(SELECTOR_NEXT);

      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'next');
        container.appendChild(el);
      }

      el.setAttribute('href', url);

    } else {
      delete container.dataset.nextUrl;

      const el =
        container.querySelector(SELECTOR_NEXT);

      if (el) el.remove();
    }
  }


  function normalizeNext(container) {
    const local =
      container
        .querySelector(SELECTOR_NEXT)
        ?.getAttribute('href');

    const fromHead =
      document
        .querySelector('head ' + SELECTOR_NEXT)
        ?.getAttribute('href');

    const url =
      local || fromHead || null;

    setNextURL(container, url);
  }


  /* =========================================================
     INDICATEUR UX
     ========================================================= */

  function ensureStatus(container) {
    let status =
      container.querySelector('#infinite-scroll-status');

    if (!status) {
      status = document.createElement('div');
      status.id = 'infinite-scroll-status';

      status.setAttribute(
        'aria-live',
        'polite'
      );

      status.style.cssText = [
        'display:none',
        'width:100%',
        'text-align:center',
        'padding:28px 15px 32px',
        'font-size:14px',
        'line-height:1.4',
        'opacity:.78',
        'box-sizing:border-box'
      ].join(';');

      const sentinel =
        container.querySelector(SELECTOR_SENTINEL);

      if (sentinel) {
        container.insertBefore(status, sentinel);
      } else {
        container.appendChild(status);
      }
    }

    return status;
  }


  function showLoadingStatus(container) {
    if (isRestoring) return;

    const status = ensureStatus(container);

status.innerHTML =
  '<span class="mm-infinite-loader" aria-hidden="true"></span>' +
  '<span>' +
  (container.dataset.textLoading || 'Chargement des produits suivants…') +
  '</span>';

    status.style.display = 'flex';
    status.style.alignItems = 'center';
    status.style.justifyContent = 'center';
    status.style.gap = '10px';

    ensureLoaderStyle();
  }


  function hideStatus(container) {
    const status =
      container.querySelector('#infinite-scroll-status');

    if (!status) return;

    status.style.display = 'none';
    status.innerHTML = '';
  }


  function showEndStatus(container) {
    const grid =
      container.querySelector(SELECTOR_GRID);

    if (!grid) return;

    const loaded =
      grid.querySelectorAll('li.grid__item').length;

    /*
     * Pas besoin d'afficher "fin" sur une petite collection
     * qui tient entièrement sur la première page.
     */
    if (loaded <= 42) {
      hideStatus(container);
      return;
    }

    const status = ensureStatus(container);

    const lang =
      document.documentElement.lang || '';

status.textContent =
  container.dataset.textEnd ||
  'Vous avez vu tous les produits de cette collection.';

    status.style.display = 'block';
  }


  function ensureLoaderStyle() {
    if (
      document.getElementById(
        'mm-infinite-loader-style'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'mm-infinite-loader-style';

    style.textContent = `
      .mm-infinite-loader {
        width: 18px;
        height: 18px;
        display: inline-block;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: mmInfiniteSpin .7s linear infinite;
        box-sizing: border-box;
      }

      @keyframes mmInfiniteSpin {
        to {
          transform: rotate(360deg);
        }
      }
    `;

    document.head.appendChild(style);
  }


  /* =========================================================
     INFRASTRUCTURE
     ========================================================= */

  function ensureInfra(container) {
    let sentinel =
      container.querySelector(SELECTOR_SENTINEL);

    if (!sentinel) {
      sentinel = document.createElement('div');

      sentinel.id =
        'infinite-scroll-sentinel';

      sentinel.style.height = '1px';

      sentinel.setAttribute(
        'aria-hidden',
        'true'
      );

      container.appendChild(sentinel);
    }

    /*
     * On conserve le spinner historique pour ne rien casser
     * dans le thème, même si notre status UX est désormais
     * l'indicateur principal.
     */
    if (
      !container.querySelector(
        '#infinite-spinner'
      )
    ) {
      const spinner =
        document.createElement('div');

      spinner.id =
        'infinite-spinner';

      spinner.className =
        'loading-overlay__spinner';

      spinner.hidden = true;

      container.appendChild(spinner);
    }

    ensureStatus(container);
  }


  /* =========================================================
     FETCH D'UNE PAGE
     ========================================================= */

  async function fetchPage(url) {
    if (!url) return null;

    const res = await fetch(url, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error(
        'HTTP ' + res.status
      );
    }

    const html =
      await res.text();

    const doc =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const newGrid =
      doc.querySelector(SELECTOR_GRID);

    if (!newGrid) {
      throw new Error(
        'Grille produit introuvable'
      );
    }

    const items =
      Array.from(
        newGrid.querySelectorAll(
          'li.grid__item'
        )
      );

    const nextURL =
      doc
        .querySelector(SELECTOR_NEXT)
        ?.getAttribute('href') || null;

    return {
      url: url,
      items: items,
      nextURL: nextURL
    };
  }


  /* =========================================================
     PRÉCHARGEMENT SILENCIEUX
     ========================================================= */

  async function prefetchNext(container) {
    if (
      reachedEnd ||
      isRestoring
    ) {
      return null;
    }

    const nextURL =
      getNextURL(container);

    if (!nextURL) {
      reachedEnd = true;
      return null;
    }

    const normalized =
      normalizeURL(nextURL);

    /*
     * Le bon lot est déjà en mémoire.
     */
    if (
      prefetchedPage &&
      normalizeURL(prefetchedPage.url) === normalized
    ) {
      return prefetchedPage;
    }

    /*
     * Un préchargement est déjà en cours.
     */
    if (prefetchPromise) {
      return prefetchPromise;
    }

    prefetchPromise = (async () => {
      try {
        const page =
          await fetchPage(nextURL);

        /*
         * Vérification : pendant le fetch, un autre mécanisme
         * pourrait avoir changé nextUrl.
         */
        const currentNext =
          getNextURL(container);

if (
  currentNext &&
  normalizeURL(currentNext) === normalized
) {
  prefetchedPage = page;

  /*
   * Précharge les images des 12 premiers produits
   * du prochain lot (2 lignes desktop × 6).
   *
   * Cela évite les trous visuels lorsque le lot
   * est ensuite injecté dans la grille.
   */
  const firstItems = page.items.slice(0, 12);

  firstItems.forEach(item => {
    const images = item.querySelectorAll('img');

    images.forEach(img => {
      const src =
        img.currentSrc ||
        img.getAttribute('src') ||
        img.getAttribute('data-src');

      const srcset =
        img.getAttribute('srcset') ||
        img.getAttribute('data-srcset');

      if (srcset) {
        const preloadImg = new Image();
        preloadImg.srcset = srcset;
        preloadImg.sizes =
          img.getAttribute('sizes') || 'auto';

        if (src) {
          preloadImg.src = src;
        }
      } else if (src) {
        const preloadImg = new Image();
        preloadImg.src = src;
      }
    });
  });
}

        return page;

      } catch (e) {
        console.warn(
          '[infinite-collection] préchargement impossible:',
          e
        );

        return null;

      } finally {
        prefetchPromise = null;
      }
    })();

    return prefetchPromise;
  }


  function schedulePrefetch(container) {
    if (
      reachedEnd ||
      isRestoring ||
      !getNextURL(container)
    ) {
      return;
    }

    /*
     * requestIdleCallback quand disponible :
     * on profite d'un moment où le navigateur est tranquille.
     */
    const start = () => {
      if (
        !reachedEnd &&
        !isRestoring
      ) {
        prefetchNext(container);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        start,
        {
          timeout: PREFETCH_DELAY + 1000
        }
      );
    } else {
      setTimeout(
        start,
        PREFETCH_DELAY
      );
    }
  }


  /* =========================================================
     AJOUT D'UN LOT DANS LA GRILLE
     ========================================================= */

  function appendPage(
    container,
    page
  ) {
    if (!page) return false;

    const grid =
      container.querySelector(SELECTOR_GRID);

    if (!grid) return false;

page.items.forEach((li, index) => {

  /*
   * Les 12 premiers produits du nouveau lot vont être
   * immédiatement visibles ou très proches du viewport.
   * On ne les laisse donc pas attendre le lazy-loading.
   */
  if (index < 12) {
    li.querySelectorAll('img').forEach(img => {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    });
  }

  grid.appendChild(li);
});

    loadedURLs.add(
      normalizeURL(page.url)
    );

    setNextURL(
      container,
      page.nextURL
    );

    prefetchedPage = null;

    updateCount(grid);

    if (!page.nextURL) {
      reachedEnd = true;

      if (observer) {
        const sentinel =
          container.querySelector(
            SELECTOR_SENTINEL
          );

        if (sentinel) {
          observer.unobserve(sentinel);
        }
      }

      showEndStatus(container);

    } else {
      reachedEnd = false;
      hideStatus(container);
    }

    return true;
  }


  /* =========================================================
     CHARGEMENT / AFFICHAGE DU LOT SUIVANT
     ========================================================= */

  async function loadNext(
    container,
    options = {}
  ) {
    const silent =
      options.silent === true;

    if (
      loading ||
      reachedEnd
    ) {
      return false;
    }

    const nextURL =
      getNextURL(container);

    if (!nextURL) {
      reachedEnd = true;

      if (!isRestoring) {
        showEndStatus(container);
      }

      return false;
    }

    const normalizedNext =
      normalizeURL(nextURL);

    if (
      loadedURLs.has(normalizedNext)
    ) {
      reachedEnd = true;
      return false;
    }

    loading = true;

    const oldSpinner =
      container.querySelector(
        '#infinite-spinner'
      );

    if (
      !silent &&
      !isRestoring
    ) {
      showLoadingStatus(container);
    }

    if (oldSpinner) {
      oldSpinner.hidden = true;
    }

    try {
      let page = null;

      /*
       * CAS IDÉAL :
       * le lot suivant a déjà été téléchargé silencieusement.
       */
      if (
        prefetchedPage &&
        normalizeURL(prefetchedPage.url) ===
          normalizedNext
      ) {
        page = prefetchedPage;

      } else if (prefetchPromise) {

        /*
         * Un téléchargement silencieux est déjà en cours.
         * On l'attend au lieu de lancer une deuxième requête.
         */
        const pending =
          await prefetchPromise;

        if (
          pending &&
          normalizeURL(pending.url) ===
            normalizedNext
        ) {
          page = pending;
        }
      }

      /*
       * Aucun cache utilisable :
       * téléchargement normal.
       */
      if (!page) {
        page =
          await fetchPage(nextURL);
      }

      /*
       * Vérifie que l'URL n'a pas changé entre temps.
       */
      const currentNext =
        getNextURL(container);

      if (
        !currentNext ||
        normalizeURL(currentNext) !==
          normalizedNext
      ) {
        return false;
      }

      const appended =
        appendPage(
          container,
          page
        );

      /*
       * Dès que ce lot est affiché,
       * on prépare silencieusement le suivant.
       */
      if (
        appended &&
        !reachedEnd &&
        !isRestoring
      ) {
        schedulePrefetch(container);
      }

      return appended;

    } catch (e) {
      console.error(
        '[infinite-collection] error:',
        e
      );

      prefetchedPage = null;

      return false;

    } finally {
      loading = false;

      if (
        !reachedEnd &&
        !isRestoring
      ) {
        hideStatus(container);
      }
    }
  }


  /* =========================================================
     RESTAURATION APRÈS RETOUR NAVIGATEUR
     ========================================================= */

  async function restoreCollectionPosition(
    container
  ) {
    if (
      !cameFromHistoryNavigation()
    ) {
      return false;
    }

    const state =
      getSavedCollectionState();

    if (!state) return false;

    const grid =
      container.querySelector(
        SELECTOR_GRID
      );

    if (!grid) return false;

    isRestoring = true;

    /*
     * On évite de montrer :
     * page 1 → page 2 → page 3 → saut.
     */
    grid.style.visibility =
      'hidden';

    if (
      'scrollRestoration' in history
    ) {
      history.scrollRestoration =
        'manual';
    }

    try {
      let loaded =
        grid.querySelectorAll(
          'li.grid__item'
        ).length;

      /*
       * Recharge les lots nécessaires.
       */
      while (
        loaded < state.loadedCount &&
        !reachedEnd
      ) {
        const before = loaded;

        const success =
          await loadNext(
            container,
            { silent: true }
          );

        loaded =
          grid.querySelectorAll(
            'li.grid__item'
          ).length;

        if (
          !success ||
          loaded <= before
        ) {
          break;
        }
      }

      /*
       * On privilégie scrollY car il restitue exactement
       * l'écran que le client avait quitté.
       */
      if (
        typeof state.scrollY === 'number' &&
        state.scrollY >= 0
      ) {
        window.scrollTo(
          0,
          state.scrollY
        );

      } else {
        const productItem =
          findSavedProduct(
            grid,
            state.productUrl
          );

        if (productItem) {
          productItem.scrollIntoView({
            behavior: 'auto',
            block: 'center'
          });
        }
      }

      await nextFrame();

      /*
       * Deuxième correction après calcul définitif
       * des dimensions.
       */
      if (
        typeof state.scrollY === 'number' &&
        state.scrollY >= 0
      ) {
        window.scrollTo(
          0,
          state.scrollY
        );
      }

      grid.style.visibility = '';

      hideStatus(container);

      /*
       * Le retour est terminé :
       * on prépare déjà le prochain lot.
       */
      isRestoring = false;

      if (!reachedEnd) {
        schedulePrefetch(container);
      }

      return true;

    } catch (e) {
      console.error(
        '[infinite-collection] erreur restauration:',
        e
      );

      grid.style.visibility = '';
      hideStatus(container);

      return false;

    } finally {
      isRestoring = false;
    }
  }


  /* =========================================================
     DÉTECTION APPROCHE BAS DE PAGE
     ========================================================= */

  function nearBottom() {
    return (
      window.innerHeight +
      window.scrollY
    ) >= (
      document.documentElement.scrollHeight -
      LOAD_AHEAD_PX
    );
  }


  function throttle(
    fn,
    wait
  ) {
    let last = 0;

    return function () {
      const now = Date.now();

      if (
        now - last >= wait
      ) {
        last = now;

        fn.apply(
          this,
          arguments
        );
      }
    };
  }


  /* =========================================================
     BIND
     ========================================================= */

  function bind(container) {
    if (!container) return;

    /*
     * Empêche les doubles bindings lors d'un re-render
     * Shopify.
     */
    if (
      container.dataset
        .infiniteScrollBound === 'true'
    ) {
      return;
    }

    container.dataset
      .infiniteScrollBound = 'true';

    const grid =
      container.querySelector(
        SELECTOR_GRID
      );

    if (!grid) return;

    const sentinel =
      container.querySelector(
        SELECTOR_SENTINEL
      );


    /*
     * INTERSECTION OBSERVER
     *
     * 1800px avant le bas :
     * les produits doivent être prêts avant que le client
     * voie un espace vide.
     */
    if (sentinel) {
      observer =
        new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (
                entry.isIntersecting &&
                !isRestoring
              ) {
                loadNext(container);
              }
            });
          },
          {
            rootMargin:
              `${LOAD_AHEAD_PX}px 0px ${LOAD_AHEAD_PX}px 0px`,
            threshold: 0
          }
        );

      observer.observe(sentinel);
    }


    /*
     * FALLBACK SCROLL
     */
    const onScroll =
      throttle(() => {
        if (
          nearBottom() &&
          !isRestoring
        ) {
          loadNext(container);
        }
      }, 120);

    window.addEventListener(
      'scroll',
      onScroll,
      { passive: true }
    );


    /*
     * MÉMORISATION LORS DU CLIC PRODUIT
     */
    grid.addEventListener(
      'click',
      function (e) {
        const link =
          e.target.closest(
            'a[href*="/products/"]'
          );

        if (!link) return;

        saveCollectionState(
          link.href
        );
      }
    );


    updateCount(grid);


    /*
     * On tente d'abord une restauration.
     *
     * Si ce n'est pas un retour navigateur,
     * on lance simplement le préchargement.
     */
    restoreCollectionPosition(
      container
    ).then(restored => {
      if (
        !restored &&
        !reachedEnd
      ) {
        schedulePrefetch(container);
      }
    });
  }


  /* =========================================================
     INIT
     ========================================================= */

  function init() {
    const container =
      document.querySelector(
        SELECTOR_CONTAINER
      );

    if (!container) return;

    loading = false;
    reachedEnd = false;

    loadedURLs.clear();

    prefetchedPage = null;
    prefetchPromise = null;

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    /*
     * Lors d'un re-render Shopify,
     * le nouveau conteneur doit pouvoir être bindé.
     */
    delete container.dataset
      .infiniteScrollBound;

    totalProducts =
      parseIntSafe(
        container.getAttribute(
          'data-total-products'
        )
      ) ||
      extractTotalFromExistingText() ||
      0;

    ensureInfra(container);

    normalizeNext(container);

    bind(container);
  }


  document.addEventListener(
    'DOMContentLoaded',
    init
  );

  document.addEventListener(
    'shopify:section:load',
    init
  );

  document.addEventListener(
    'shopify:section:rendered',
    init
  );

})();


/* =========================================================
   TRI SIMPLE
   ========================================================= */

document.addEventListener(
  'change',
  function (e) {
    if (
      e.target &&
      e.target.id === 'SortBySimple'
    ) {
      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        'sort_by',
        e.target.value
      );

      /*
       * Toujours repartir page 1 après changement de tri.
       */
      url.searchParams.delete(
        'page'
      );

      window.location.assign(
        url.toString()
      );
    }
  }
);


/* =========================================================
   COMPTEUR TOP
   ========================================================= */

(function () {

  function updateTopCount() {
    const grid =
      document.querySelector(
        '#product-grid'
      );

    const top =
      document.getElementById(
        'ProductCountTop'
      );

    if (
      !grid ||
      !top
    ) {
      return;
    }

    const loaded =
      grid.querySelectorAll(
        'li.grid__item'
      ).length;

    let total = 0;

    const container =
      document.querySelector(
        '[data-infinite-container]'
      );

    if (
      container &&
      container.dataset.totalProducts
    ) {
      total =
        parseInt(
          container.dataset.totalProducts,
          10
        ) || 0;
    }

    top.textContent =
      total > 0
        ? `${loaded} sur ${total}`
        : `${loaded}`;
  }


  document.addEventListener(
    'DOMContentLoaded',
    updateTopCount
  );

  document.addEventListener(
    'shopify:section:load',
    updateTopCount
  );

  document.addEventListener(
    'shopify:section:rendered',
    updateTopCount
  );

  window.__updateTopCount =
    updateTopCount;

})();